import React, { useEffect, useState, useRef, useCallback } from 'react'
import { IconPencilEdit } from '@edulastic/icons'
import { greenDarkSecondary } from '@edulastic/colors'
import { Tooltip } from 'antd'
import { tutorials } from '@edulastic/constants'
import { EduJoyride } from '@edulastic/common'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { ACTIONS, LIFECYCLE } from 'react-joyride'
import {
  StyledPencilIconContainer,
  StyledPopoverContainer,
  StyledPopoverContentContainer,
  StyledChildrenContainer,
  StyledChangeQTypeButton,
} from './styled'
import SelectQTypeOption from './SelectQTypeOption'
import {
  setViewedTutorialsAction,
  viewedTutorialsSelector,
} from '../../../../tutorials/tutorialReducer'
import { ITEM_DETAILS_CHANGE_QUESTION_TYPE_TUTORIAL_STEPS } from '../../../src/constants/others'
import { onEnterKeyPressHandler } from '../../../../common/utils/helpers'

const ChangeQType = ({
  children,
  onQuestionTypeSelect,
  handlePopupVisibilityChange,
  isQuestionTypeChangePopupVisible,
  viewedTutorials,
  setViewedTutorials,
}) => {
  const popoverContentRef = useRef(null) // Ref for the popover's content container
  const changeQTypeButtonRef = useRef(null) // Ref for the change question type button to return focus
  const [isKeyboardUser, setIsKeyboardUser] = useState(false)
  const changeQTypeTutorialCallback = (data) => {
    const { COMPLETE } = LIFECYCLE
    const { CLOSE, NEXT } = ACTIONS

    if (
      data.lifecycle === COMPLETE &&
      (data.action === CLOSE || data.action === NEXT)
    ) {
      setViewedTutorials([
        ...viewedTutorials,
        tutorials.itemDetailsChangeQuestionType,
      ])
    }
  }

  useEffect(() => {
    const handleEscapeKeyPress = (event) => {
      if (event.key === 'Escape') {
        handlePopupVisibilityChange(false)
        setIsKeyboardUser(false)
      }
    }

    window.addEventListener('keydown', handleEscapeKeyPress)

    return () => {
      window.removeEventListener('keydown', handleEscapeKeyPress)
    }
  }, [])
  // Memoize the trapFocus function to avoid recreating it on every render
  const trapFocus = useCallback(
    (e) => {
      if (e.key === 'Tab' && popoverContentRef.current) {
        const focusableElements = popoverContentRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )

        if (focusableElements.length === 0) return // No focusable elements to trap

        const firstFocusableElement = focusableElements[0]
        const lastFocusableElement =
          focusableElements[focusableElements.length - 1]

        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault() // Prevent default Tab behavior
            handlePopupVisibilityChange(false) // Close popover
          }
        } else if (document.activeElement === lastFocusableElement) {
          e.preventDefault() // Prevent default Tab behavior
          handlePopupVisibilityChange(false) // Close popover
        }
      }
      if (e.key === 'Escape' && isQuestionTypeChangePopupVisible) {
        e.preventDefault()
        handlePopupVisibilityChange(false)
      }
    },
    [isQuestionTypeChangePopupVisible, handlePopupVisibilityChange]
  )

  useEffect(() => {
    if (isQuestionTypeChangePopupVisible) {
      // Focus the first tabbable element inside the popover when it opens
      const timeoutId = setTimeout(() => {
        if (popoverContentRef.current) {
          const focusableElements = popoverContentRef.current.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
          if (focusableElements.length > 0) {
            focusableElements[0].focus()
          }
        }
      }, 50)

      document.addEventListener('keydown', trapFocus)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('keydown', trapFocus)
      }
    }
    // Return focus to the change question type button when the popover closes
    if (changeQTypeButtonRef.current) {
      changeQTypeButtonRef.current.focus()
    }
    document.removeEventListener('keydown', trapFocus)
  }, [isQuestionTypeChangePopupVisible, trapFocus])
  return (
    <span data-joyride-id="changeQuestionType">
      <Tooltip
        title="Click to change question type"
        placement="right"
        trigger={['hover', 'focus']}
      >
        <StyledPopoverContainer
          placement="bottomLeft"
          trigger={['click']}
          visible={isQuestionTypeChangePopupVisible}
          onVisibleChange={(value) => {
            handlePopupVisibilityChange(value)
          }}
          content={
            <StyledPopoverContentContainer ref={popoverContentRef}>
              <SelectQTypeOption
                isKeyboardUser={isKeyboardUser}
                onQuestionTypeSelect={onQuestionTypeSelect}
                isQuestionTypeChangePopupVisible={
                  isQuestionTypeChangePopupVisible
                }
                onClosePopover={() => handlePopupVisibilityChange(false)}
              />
            </StyledPopoverContentContainer>
          }
        >
          <StyledChangeQTypeButton
            ref={changeQTypeButtonRef}
            role="button"
            aria-expanded={!!isQuestionTypeChangePopupVisible}
            aria-label={`${children}, Click to change question type`}
            onKeyDown={(e) =>
              onEnterKeyPressHandler(e, () => {
                handlePopupVisibilityChange(true)
                setIsKeyboardUser(true)
              })
            }
            onClick={() => {
              setIsKeyboardUser(false)
              handlePopupVisibilityChange(!isQuestionTypeChangePopupVisible)
            }}
            tabIndex="0"
          >
            <StyledChildrenContainer>{children}</StyledChildrenContainer>
            <StyledPencilIconContainer aria-hidden="true">
              <IconPencilEdit color={greenDarkSecondary} />
            </StyledPencilIconContainer>
          </StyledChangeQTypeButton>
        </StyledPopoverContainer>
      </Tooltip>
      {!viewedTutorials.includes(tutorials.itemDetailsChangeQuestionType) && (
        <EduJoyride
          steps={ITEM_DETAILS_CHANGE_QUESTION_TYPE_TUTORIAL_STEPS}
          callback={changeQTypeTutorialCallback}
          styles={{
            tooltipContent: {
              paddingTop: '20px',
            },
          }}
          locale={{
            close: 'Done',
          }}
        />
      )}
    </span>
  )
}

const enhance = compose(
  connect(
    (state) => ({
      viewedTutorials: viewedTutorialsSelector(state),
    }),
    {
      setViewedTutorials: setViewedTutorialsAction,
    }
  )
)

export default enhance(ChangeQType)
